import { Institution } from './institutions.types';
import { StaffMember } from './staffMembers.types';

export enum LeadStatus {
  NEW = 'חדש',
  OPEN_INQUIRY = 'פנייה פתוחה',
  NO_ANSWER = 'לא ענו',
  REQUESTED_CALLBACK = 'ביקשו שיחזרו אליהם',
  IN_CONVERSATION_REQUESTED_DETAILS = 'בשיחה - ביקשו פרטים נוספים',
  INTERESTED_IN_REGISTRATION_IN_PROCESS = 'מעוניינים להירשם - בתהליך',
  REGISTERED_FOR_EVENT = 'נרשמו לאירוע/ערב התעניינות',
  REGISTERED_ON_MUNICIPALITY_SITE = 'נרשמו באתר העירייה',
  REGISTRATION_COMPLETED = 'הושלם רישום',
  NOT_INTERESTED = 'לא מעוניינים',
  NOT_RELEVANT = 'לא רלוונטי (עברו דירה/גיל לא מתאים)',
  ON_HOLD = 'בהשהיה',
  CLOSED = 'סגור'
}

// New enums for enrollment fields
export enum ReligiousStatus {
  SECULAR = 'חילוני',
  TRADITIONAL = 'מסורתי',
  RELIGIOUS = 'דתי',
  ULTRA_ORTHODOX = 'חרדי'
}

export enum HasSibling {
  YES = 'כן',
  NO = 'לא'
}

export enum GradeLevel {
  PRE_PRE = 'טרום טרום',
  PRE = 'טרום',
  MANDATORY = 'חובה',
  // Shalhavot specific grade levels
  GRADE_A = 'א',
  GRADE_B = 'ב',
  GRADE_C = 'ג',
  GRADE_D = 'ד',
  GRADE_E = 'ה',
  GRADE_F = 'ו'
}

export enum Gender {
  MALE = 'זכר',
  FEMALE = 'נקבה'
}

export enum ArrivalSource {
  FACEBOOK = 'פייסבוק',
  INSTAGRAM = 'אינסטגרם',
  TIKTOK = 'טיקטוק',
  GOOGLE = 'גוגל',
  FRIEND_REFERRAL = 'חבר מביא חבר',
  OTHER = 'אחר'
}

// New enum for Shalhavot-specific "Has sibling in Shalhavot"
export enum HasSiblingShalhavot {
  YES = 'כן',
  NO = 'לא'
}

export enum LeadsTargetType {
  KINDERGARTEN_LEADS = 'kindergarten-leads',
  SHALHAVOT = 'shalhavot',
  DAYCARE_CENTERS = 'daycare-centers'
}

// Interface for Kindergarten-specific fields
export interface ILeadKindergarten {
  hasSiblingInChabad?: string; // אח/ות בגן חב"ד
  gradeLevel?: string; // שכבת גיל (טרום טרום, טרום, חובה)
}

// Interface for Shalhavot-specific fields
export interface ILeadShalhavot {
  hasSiblingShalhavot?: string; // אח/ות שלומד או למד בשלהבות
  mandatoryKindergartenName?: string; // שם גן חובה [לעולים לא']
  previousSchoolName?: string; // שם בית ספר קודם [למצטרפים מב' ואלך]
  upgradeToGrade?: string; // עולה לכיתה [א / ב / ג / ד / ה / ו]
  botCallSummary?: string; // סיכום שיחת בוט
}

export type Lead = {
  id: number;
  operationId: number;
  staffMemberId: number;
  institutionId?: number;
  status: LeadStatus;
  isNew: boolean;
  // Child and parent specific fields for reshet enrollment
  name?: string; // Combined name field for backward compatibility
  childFirstName?: string;
  parentFirstName?: string;
  lastName?: string;
  parentLastName?: string;
  parentPhone?: string;
  parentEmail?: string;
  address?: string;
  city?: string;
  neighborhood?: string;
  comment?: string;
  isTransfer: boolean;
  // Institution type for filtering leads by type
  institutionType?: string; // גן, בית ספר, על יסודי, בית ספר שלהבות
  // New comprehensive enrollment fields
  religiousStatus?: string; // חילוני, מסורתי, דתי, חרדי
  childBirthdate?: string;
  childAge?: number; // Computed dynamically from birthdate on backend
  gender?: string; // זכר, נקבה
  arrivalSource?: string; // פייסבוק, אינסטגרם, טיקטוק, גוגל, חבר מביא חבר, אחר
  specialNeeds?: string;
  initialCallSummary?: string;
  academicYear?: string; // Default: תשפ"ו
  lastStatusChangeDate?: string;
  // Unified distance field for all institution types
  distanceKmFromInstitution?: number; // מרחק מהמוסד בקילומטרים
  createdAt: string;
  updatedAt: string;
  StaffMember?: StaffMember;
  institution?: Institution;
  geom?: any;
  isSentFailed?: boolean;
  Operation?: Operation;
  isCampaign: boolean;
  check?: boolean;
  source?: string;
  referenceuUrl?: string;
  sourceType?: 'personal' | 'campaign' | 'organic' | 'paid';
} & ILeadKindergarten &
  ILeadShalhavot;

export type Operation = {
  id: number;
  operationName: string;
  iconUrl?: string;
  isActive: boolean;
  isLead: boolean;
  startAt: string;
  endAt: string;
  sendWaToStaffMemberId: number;
};

export type OperationToTeacher = {
  id: number;
  operationId: number;
  staffMemberId: number;
  fromStaffMemberId: number;
  Operation?: Operation;
  StaffMembers?: StaffMember[];
};

export type Tag = {
  tag: string;
  hasPermissionPages: string;
  isAdmin: boolean;
  isAdminInstitutions: boolean;
  operations: number[];
};

export type Donation = Lead & {
  amount: string;
  isCreditReceived: boolean;
  link: string;
};

export type Purchase = Donation & {
  products: string;
};

export type Event = {
  id: number;
  operationId: number;
  shaliachId: number;
  institutionId: number;
  title: string;
  description: string;
  date: string;
  fromDate: string;
  toDate: string;
  time: string;
  fromTime: string;
  toTime: string;
  address: string;
  content: string;
  phone: string;
  link: string;
  isActive: boolean;
  geom: any;
  isSaleMalash: string;
  Operation?: Operation;
  StaffMember?: StaffMember;
  Institution?: Institution;
  thumbnail?: string;
  image?: string;
  createdAt: string;
  updatedAt: string;
};

export type Order = {
  id: number;
  StaffMember?: StaffMember;
  staffMemberId: number;
  productId: string;
  name: string;
  price: number;
  isCreditReceived: boolean;
  createdAt: string;
  updatedAt: string;
  creditInTheMLS?: boolean;
};

export type StamChecks = {
  id: number;
  orderId: number;
  itemType: 'תפילין' | 'מזוזות' | 'החלפת רצועות' | 'ריקמה' | 'הטבעה' | 'קשירת ציציות' | 'כריכת ספר';
  quantity: number;
  soferName?: string;
  magihName?: string;
  checkType: 'פסול' | 'קלות' | 'הידור' | 'תיוג';
  fixType: 'פסול' | 'קלות' | 'הידור' | 'תיוג';
  notes?: string;
  status: 'חדש' | 'בטיפול' | 'בוטל' | 'מעוכב' | 'בבירור' | 'טופל' | 'עבר למגיה' | 'הועבר לסגירה' | 'נמסר ללקוח';
  internalComments?: string;
  checkerName?: string;
  checkSummary?: string;
  writingNotes?: string;
  soferNotes?: string;
  magihNotes?: string;
  problemsSummary?: string;
  totalPsulProblems?: number;
  totalKalotProblems?: number;
  totalHidurProblems?: number;
  totalTiyugProblems?: number;
  cost?: string;
  alternativeProduct?: string;
  paid?: string;
  fixesMade?: string;
  itemsReplaced?: string;
  resultsNotes?: string;
  retzuotType?: 'שחור דו צדדי' | 'שחור צד אחד';
  retzuotSide?: 'ימין' | 'שמאל';
  rikmaColor?: string;
  rikmaMaterial?: string;
  hatbaaText?: string;
  tzitzitType?: 'דק' | 'עבה';
  kshiraType?: 'ספרדי' | 'אשכנזי' | 'חב"ד';
  createdAt: Date;
  updatedAt: Date;
  fileUrl?: string;
};

export type StamOrders = {
  id: number;
  staffMemberId: number;
  firstName: string;
  lastName: string;
  phone: string;
  mail?: string;
  address?: string;
  StamChecks: StamChecks[];
  StaffMember?: StaffMember;
  createdAt: string;
  updatedAt: string;
};

export type EventConfiguration = {
  id: number;
  operationId: number;
  isTitle: boolean;
  isDescription: boolean;
  isDate: boolean;
  isRangeDate: boolean;
  isTime: boolean;
  isRangeTime: boolean;
  isAddress: boolean;
  isContent: boolean;
  isPhone: boolean;
  isLink: boolean;
  isSaleMalash: boolean;
  isImage: boolean;
  defaultText?: string;
  Operation?: Operation;
};

export type Comment = {
  id: number;
  postId: number;
  staffMemberId: number;
  comment: string;
  createdAt: string;
  updatedAt: string;
  StaffMember: {
    firstName: string;
    lastName: string;
    profileImage: string;
  };
};

export type Like = {
  id: number;
  postId: number;
  staffMemberId: number;
};

export type Post = {
  id: number;
  content: string;
  department: string;
  createdAt: string;
  updatedAt: string;
  category: string;
  FeedLikes: Like[];
  FeedComments: Comment[];
  StaffMember?: StaffMember;
  isPublishApproved: boolean;
  tagsAllow: string[];
};

export type Contact = Lead | Donation | Purchase;
